import { useRenterDashboardContext } from "../../renter-dashboard/useRenterDashboardContext";
import Loading from "@/common/components/ui/loading/Loading";
import AditionalValuesCancelPlan from "../../modals/fields/EditBondRenterVehcile/formsModal/field/AdditionalValuesCancelPlan";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { Info } from "lucide-react";
import { BILLING_TYPE } from "@/types/payment.type";
import { FormikProvider } from "formik";
import { PaymentOptionsSection } from "../../negotiation-debits/components/payments/PaymentOptionsSection";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useRescission } from "../customhook/useRescission";
import InputMoney from "@/common/components/form/CurrencyInputFormik";
import { formatDateAndTime } from "@/common/helpers/date.helpers";

function getReasonLabelsRepossession(reason: string) {
  const reasonMap: Record<string, string> = {
    "VEHICLE_EXCHANGE": "Troca de moto",
    "ACCIDENT": "Acidente",
    "UNDEFINED": "Indefinido",
    "DEFAULT": "Inadimplencia",
    "SUSPENDED_LICENSE": "Cnh suspensa",
    "CARD_REFUSAL": "Recusa do cartão",
    "PLAN_COMPLETION": "Finalizar plano",
    "PLAN_CANCELLATION": "Cancelar plano",
    "WITHDRAWAL": "Desistencia",
    "PLAN_FREEZE": "Congelamento",
  };

  return reasonMap[reason] || reason;
}

export default function RescissionDetails() {
  const { renter } = useRenterDashboardContext();

  const {
    isLoading,
    rentalHistory,
    daysRemaining,
    finePlanValue,
    damagedPartsTotal,
    amountValue,
    handleTotalChangeWrapper,
    paymentFormik,
    valueWeeks,
    dailyValue,
    // caucao,
    payments,
    vehicleVisualRecords,
    manualAdjustment,
    isAddingAdjustment,
    selectedPayments,
    selectedPaymentsTotal,
    handleTogglePayment,
    handleManualAdjustmentChange,
    handleToggleAdjustmentType,
    partnersMap,
  } = useRescission({ renter });


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loading />
      </div>
    );
  }

  if (rentalHistory.length === 0) {
    return (
      <main className="p-6 w-full max-w-7xl mx-auto min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">Detalhes da Rescisão</h1>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-6">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-yellow-800 font-medium">Nenhum histórico de rescisão encontrado para este cliente.</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="rescission-details" className="p-6 w-full max-w-8xl mx-auto min-h-screen bg-gray-50">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Detalhes da Rescisão</h1>
        <p className="text-gray-600">Informações completas sobre o processo de rescisão do contrato</p>
      </div>

      <div className="space-y-6">
        {rentalHistory?.map((rental) => (
          <div key={rental._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-redPrimary px-6 py-4">
              <h2 className="text-xl font-semibold text-white">{rental.renterName || 'N/A'}</h2>
              <p className="text-blue-100 text-sm mt-1">ID da Locação: {rental._id}</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Informações do Cliente e Plano */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-5 h-full border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Info className="w-5 h-5 mr-2 text-red" />
                      Informações do Plano
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Plano Contratado</p>
                        <p className="text-gray-900 font-semibold">{renter.activationData.plan.label || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Valor Semanal</p>
                        <p className="text-gray-900 font-semibold text-lg">{formatCurrency(renter.activationData.plan.value) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Valor do Caução</p>
                        <p className="text-gray-900 font-semibold text-lg">{formatCurrency(renter.activationData.plan.deposit) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Data do Recolhimento</p>
                        <p className="text-gray-900 font-medium">
                          {rental.unlinkedAt
                            ? new Date(rental.unlinkedAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Motivo</p>
                        <p className="text-gray-900 font-medium">{rental.reason ? getReasonLabelsRepossession(rental.reason) : 'Término do contrato'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Parceiro</p>
                        <p className="text-gray-900 font-semibold">
                          {rental.licensePlate && partnersMap[rental.licensePlate]
                            ? partnersMap[rental.licensePlate].partnerCompanyName
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Valores da Rescisão */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-5 h-full border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                      </svg>
                      Valores e Multas
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Valor da Multa</p>
                        <p className="text-red-700 font-bold text-2xl">R$ {finePlanValue}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dias Restantes</p>
                        <p className="text-gray-900 font-semibold">{daysRemaining} dias</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Peças Avariadas</p>
                        <p className={`font-semibold ${rental.damagedParts && rental.damagedParts.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {rental.damagedParts && rental.damagedParts.length > 0
                            ? `Sim (${rental.damagedParts.length} peça${rental.damagedParts.length > 1 ? 's' : ''})`
                            : 'Não'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Valores Adicionais */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-5 h-full border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                      </svg>
                      Valores Adicionais
                    </h3>
                    <AditionalValuesCancelPlan
                      onTotalChange={handleTotalChangeWrapper}
                      valueWeeks={valueWeeks}
                      dailyFine={dailyValue}
                      finePlanValue={finePlanValue}
                    />
                  </div>
                </div>
              </div>
            </div>

            {rental.damagedParts && rental.damagedParts.length > 0 && (
              <div className="border-t border-gray-200">
                <section className="p-4">
                  <h2 className="text-xl font-semibold mb-4 text-gray-700">Peças Avariadas</h2>
                  <div className="bg-white shadow rounded p-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2">Peça</th>
                          <th className="text-right p-2">Valor Unit.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rental.damagedParts.map((part, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2">{part.name}</td>
                            <td className="p-2 text-right font-semibold">
                              R$ {(part.quantity * (part.sellPrice || 0)).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold">
                        <tr>
                          <td className="p-2 text-right">Total:</td>
                          <td className="p-2 text-right">
                            R$ {damagedPartsTotal.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </section>
              </div>

            )}
            <div>
              <section id={"repossession-history"} className="p-4 border-t border-gray-200">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Detalhe do recolhimento</h2>
                {/* Conteúdo do registro de recolhimento vai aqui: vehicleVisualRecords.reason */}
                <p className="mt-2 text-gray-700">{vehicleVisualRecords?.returnReason || 'Nenhum detalhe disponível.'}</p>
              </section>
            </div>
          </div>
        ))}

        {/* Histórico de Veículos Locados */}
        {renter.desactivationData?.vehicleHistory && renter.desactivationData.vehicleHistory.length > 0 && (
          <section id="vehicle-history" className="mb-6">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Histórico de Veículos Locados</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renter.desactivationData.vehicleHistory
                .filter((vehicle) => vehicle.licensePlate || vehicle.brand || vehicle.model || vehicle.chassi)
                .map((vehicle, index: number) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-redPrimary  px-4 py-3">
                      <h3 className="text-lg font-semibold text-white flex items-center">
                        {vehicle.licensePlate || 'Veículo sem placa'}
                      </h3>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Marca</p>
                          <p className="text-gray-900 font-medium">{vehicle.brand || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Modelo</p>
                          <p className="text-gray-900 font-medium">{vehicle.model || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">Início</p>
                            <p className="text-gray-900 text-base font-bold">
                              {vehicle.vehicleHistoryInitial
                                ? formatDateAndTime(vehicle.vehicleHistoryInitial)
                                : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">Fim</p>
                            <p className="text-gray-900 text-base font-bold">
                              {vehicle.vehicleHistoryEnd
                                ? formatDateAndTime(vehicle.vehicleHistoryEnd)
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {(vehicle.optionRepossesion && vehicle.optionRepossesion !== 'Indefinido') ||
                        (vehicle.licensePlate && partnersMap[vehicle.licensePlate]) ? (
                        <div className="pt-3 border-t border-gray-200">
                          <div className={`grid gap-3 ${(vehicle.optionRepossesion && vehicle.optionRepossesion !== 'Indefinido') &&
                              (vehicle.licensePlate && partnersMap[vehicle.licensePlate])
                              ? 'grid-cols-2'
                              : 'grid-cols-1'
                            }`}>
                            {vehicle.optionRepossesion && vehicle.optionRepossesion !== 'Indefinido' && (
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Motivo</p>
                                <p className="text-gray-900 font-medium">
                                  {getReasonLabelsRepossession(vehicle.optionRepossesion)}
                                </p>
                              </div>
                            )}

                            {vehicle.licensePlate && partnersMap[vehicle.licensePlate] && (
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Parceiro</p>
                                <p className="text-gray-900 font-semibold">
                                  {partnersMap[vehicle.licensePlate].partnerCompanyName}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        <section id={"payments-history"}>
          <div className="">
            {payments.length > 0 ? (
              <>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Histórico de Pagamentos</h2>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-center p-2 w-12">Incluir</th>
                        <th className="text-left p-2">Descrição</th>
                        <th className="text-left p-2">Data de Vencimento</th>
                        <th className="text-left p-2">Valor</th>
                        <th className="text-left p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, index) => (
                        <tr key={index} className="border-t hover:bg-gray-50">
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedPayments.includes(payment._id!)}
                              onChange={() => handleTogglePayment(payment._id!)}
                              className="w-4 h-4 text-redPrimary bg-gray-100 border-gray-300 rounded focus:ring-redPrimary focus:ring-2 cursor-pointer"
                            />
                          </td>
                          <td className="p-2">{payment.description}</td>
                          <td className="p-2">
                            {new Date(payment.dueDate!).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="p-2">R$ {parseFloat(payment.value!.toString()).toFixed(2)}</td>
                          <td className="p-2 capitalize">{payment.status}</td>
                        </tr>
                      ))}
                    </tbody>
                    {selectedPayments.length > 0 && (
                      <tfoot className="bg-blue-50 font-semibold">
                        <tr>
                          <td colSpan={3} className="p-2 text-right">Total Selecionado:</td>
                          <td className="p-2">R$ {selectedPaymentsTotal.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </>
            ) : (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-6">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-yellow-800 font-medium">Nenhum pagamento pendente encontrado para este cliente.</p>
                </div>
              </div>
            )}
          </div>

        </section>
      </div>

      {/* Seção de Pagamento */}
      <FormikProvider value={paymentFormik}>
        <form onSubmit={paymentFormik.handleSubmit} className="mt-8 space-y-6">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Resumo da Cobrança</h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Valor Base:</span>
                  <span className="text-lg font-semibold text-gray-900">
                    R$ {(parseFloat(amountValue) - manualAdjustment - selectedPaymentsTotal).toFixed(2)}
                  </span>
                </div>

                {selectedPaymentsTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">+ Pagamentos Selecionados:</span>
                    <span className="text-lg font-semibold text-blue-600">
                      R$ {selectedPaymentsTotal.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <InputMoney
                        name="manualAdjustment"
                        label="Ajuste Manual"
                        value={manualAdjustment}
                        onValueChange={handleManualAdjustmentChange}
                        placeholder="0,00"
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-2 pt-7">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isAddingAdjustment}
                          onChange={handleToggleAdjustmentType}
                          className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">Adicionar (+)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!isAddingAdjustment}
                          onChange={handleToggleAdjustmentType}
                          className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">Subtrair (-)</span>
                      </label>
                    </div>
                    <div className="flex flex-col items-end justify-center pt-7">
                      <span className="text-xs text-gray-500 mb-1">Valor do Ajuste:</span>
                      <span className={`text-xl font-bold ${isAddingAdjustment ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {isAddingAdjustment ? '+' : '-'} R$ {manualAdjustment.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t-2 border-gray-300">
                  <span className="text-gray-900 font-bold text-lg">Valor Final da Cobrança:</span>
                  <span className="text-3xl font-bold text-redPrimary">R$ {amountValue}</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Este valor inclui a multa do plano, o custo das peças avariadas (se houver), valores adicionais selecionados, pagamentos incluídos, ajuste manual, subtraindo o valor do caução (se aplicável).
              </p>
            </div>
          </section>

          <PaymentOptionsSection
            baseValue={parseFloat(amountValue)}
            amountCharged={0}
            finalValue={parseFloat(amountValue)}
            showBillingPeriod={true}
          />

          <div className="flex flex-col gap-2">
            <label htmlFor="paymentDescription" className="block text-sm font-medium text-gray-600">
              Descrição do pagamento
            </label>
            <textarea
              id="paymentDescription"
              name="paymentDescription"
              value={paymentFormik.values.paymentDescription || ''}
              onChange={(e) => paymentFormik.setFieldValue('paymentDescription', e.target.value)}
              onBlur={(e) => {
                paymentFormik.handleBlur(e);
                const val = paymentFormik.values.paymentDescription || '';
                if (val.length > 500) {
                  paymentFormik.setFieldError('paymentDescription', 'Máximo de 500 caracteres');
                } else {
                  paymentFormik.setFieldError('paymentDescription', undefined);
                }
              }}
              maxLength={500}
              style={{ resize: "none", height: "90px" }}
              className="border border-gray p-2 rounded w-full bg-[#ffffff]"
            />
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {(paymentFormik.values.paymentDescription?.length || 0)}/500
              </div>
              {paymentFormik.touched.paymentDescription && paymentFormik.errors.paymentDescription && (
                <div className="text-redPrimary text-sm">{paymentFormik.errors.paymentDescription}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <ButtonWithLoader
              isLoading={paymentFormik.isSubmitting}
              onClick={() => paymentFormik.submitForm()}
              disabled={paymentFormik.isSubmitting || paymentFormik.values.billingType === BILLING_TYPE.UNDEFINED}
            >
              Gerar Cobrança
            </ButtonWithLoader>
          </div>
        </form>
      </FormikProvider>
    </main>
  );
}